
import Vue, { PropType } from 'vue';
import { v4 as uuidv4 } from 'uuid';
import { debounce } from 'throttle-debounce';

import LogoLibrary from '../logo-library/TheApp.vue';
import {
  CvIconButton,
  CvGrid,
  CvColumn,
  CvRow,
  CvLoading,
  CvButton,
} from '@carbon/vue';
import { base64StringToBlob } from 'blob-util';
import { AnnotationShape, LogoOnlyArtwork, WizardTourStep } from './typings';
import { ProductPrintPositionAddModel } from '@/typings';
import { ArtworkPayload, TextShape } from './typings/pintura';

import w2pPintura from '@/components/w2pPintura/w2pPintura.vue';
import VueTour from '@/components/VueTour/main';
import Notification20 from '@carbon/icons-vue/es/notification/20';
import GeneratePdf16 from '@carbon/icons-vue/es/generate-pdf/16';
import { colorStringToColorArray } from 'pintura';
import customersAPI from '@/services/CustomersService';

import { mapActions, mapState, mapStores } from 'pinia';
import {
  attachActionSubscriber as attachProductActionSubscriber,
  useStore as useProductStore,
} from '@/stores/product';

import { attachActionSubscriber as attachUserActionSubscriber } from '@/stores/action';
import Ruler from './components/ruler/Ruler.vue';
import {
  attachActionSubscriber as attachShapeActionSubscriber,
  nonTextShapes,
  textShapes,
  useStore as useShapeStore,
} from '@/stores/shape';
import { useStore as useSectionStore } from '@/stores/section';
import { useStore as useNotificationStore } from '@/stores/notification';
import { useStore as usePriceSectionStore } from '@/stores/priceSection';
import { useStore as useShapeBoxSectionStore } from '@/stores/shapeBoxSection';
import { useStore as useLangStore } from '@/stores/lang';
import { useStore as useMirrorStore } from '@/stores/mirror';
import { useStore as useHistoryStore } from '@/stores/history';
import { useStore as useColorsManagementStore } from '@/stores/colorsManagement';
import { CustomToolset } from './components/customTools';
import { PinturaHeader } from './components/header';
import { PinturaSidebar } from './components/sidebar';
import { BellNotificationMonitor } from '@/components/Notifiers/index';
import { SwitchColor } from '@/components/SwitchColor/index';
import { removeAppContext, setAppContext } from '@/utils/state';
import { computed, ComputedRef } from 'vue';
import isValidLanguageValidator from '@/validators/langLocaleValidator';
import {
  PdpChoicesSetterType,
  PriceDetailsSetterType,
} from '@/setters/typings';
import {
  Category,
  CreatePersonalizationForm,
  NewProductPrintPositionForms,
  Personalization,
  ProductPrintArea,
  ProductPrintAreaPoint,
  ProductPrintPositions,
  RoundItemConfiguration,
} from '@midocean/webapps-shared-domain';
import { mixin as clickaway } from 'vue-clickaway';
import { generateEventPayload } from '@/utils/generatePriceParamsEventPayload';
import { ProductPrintPositionBoxModel } from '@/typings/ProductPrintData';
import {
  constructArtworkValidationPipeline,
  maxNumberOfColoursValidator,
  ValidationResult,
} from '@/validators/index';
import { mm2px, px2mm, recalculateDPI } from '@/utils/common';
import { SupportedLangCodes } from '../../constants/languageCodes';
import { lineWidth } from '@/constants/areaLineWidth';
import { DoSmallDetailsPart } from '@/constants/imageProcessingEngine';
import {
  ManualCheck,
  getSmallDetailsValidatorColor,
} from '../../components/SmallDetails';
import { mapSmallDetailsPayload } from '@/components/SmallDetails/utils/helper';
import { DefaultSmallDetailsResult } from '@/typings/SmallDetails';
import { createStateSnapshot } from '@/utils/historyHelper';
import { drawPanelLines } from '@/utils/drawPanelLines';
import {
  PmsColor,
  ProductPrintData,
  ProductPrintVariant,
} from '@midocean/webapps-shared-domain';
import { positionText } from '@/utils/alignText';
import {
  getArtworkFiles,
  getProofFormFromState,
} from '@/utils/personalizationHelper';
import { PipelineNotificationType } from '@/components/Notifiers/typings/PipelineNotificationType';
import { CreatePersonalizationFormWrapper } from './typings/common/createPersonalization';

Vue.use(VueTour);

export default Vue.extend({
  name: 'ImportWizard',
  components: {
    PinturaHeader,
    PinturaSidebar,
    LogoLibrary,
    w2pPintura,
    CvGrid,
    CvRow,
    CvColumn,
    CvIconButton,
    CvLoading,
    CvButton,
    CustomToolset,
    Notification20,
    BellNotificationMonitor,
    SwitchColor,
    ManualCheck,
    GeneratePdf16,
    Ruler,
  },
  mixins: [clickaway],

  props: {
    lang: {
      type: String as PropType<SupportedLangCodes>,
      required: false,
      default: 'en',
      validator: isValidLanguageValidator,
    },
    personalizationId: {
      type: String,
      required: false,
      default() {
        return '';
      },
    },
    sapCustomerId: {
      type: String,
      required: true,
    },
    tok: {
      type: String,
      required: true,
    },
    priceDetails: {
      type: Object as PropType<PriceDetailsSetterType>,
      required: true,
    },
    pdpChoices: {
      type: Object as PropType<PdpChoicesSetterType>,
      required: false,
      default: {} as PdpChoicesSetterType,
    },
    printPositions: {
      type: [] as PropType<ProductPrintPositionAddModel[]>,
      required: false,
      default() {
        return [];
      },
    },
    isDirectApproval: {
      type: Boolean,
      required: false,
      default: false,
    },
    isConceptDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    isFirstTimeOpened: {
      type: Boolean,
      required: false,
      default: false,
    },
    isOrderDisabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    triggerSmallDetails: {
      type: Boolean,
      required: false,
      default: false,
    },
    customerCompanyLogoUrl: {
      type: String,
      required: false,
      default: '',
    },
    logoOnlyArtwork: {
      type: [] as PropType<LogoOnlyArtwork[]>,
      required: false,
      default: () => {
        return [];
      },
    },
    version: {
      type: String,
      required: false,
      default: 'V1',
    },
    context: {
      type: String,
      required: false,
      default: '',
    },
    personalizationJson: {
      type: Object as PropType<CreatePersonalizationFormWrapper>,
      required: false,
      default: () => {
        return {} as CreatePersonalizationFormWrapper;
      },
    },
  },

  provide(): {
    $priceDetails: ComputedRef<PriceDetailsSetterType>;
    $pdpChoices: ComputedRef<PdpChoicesSetterType>;
    $printPositions: ComputedRef<Array<unknown>>;
    $sapCustomerId: ComputedRef<string>;
    $isEditMode: ComputedRef<boolean>;
    $personalization: ComputedRef<Personalization>;
    $isDirectApproval: ComputedRef<boolean>;
    $isFirstTimeOpened: ComputedRef<boolean>;
    $isConceptDisabled: ComputedRef<boolean>;
    $isOrderDisabled: ComputedRef<boolean>;
    $customerCompanyLogoUrl: ComputedRef<string>;
  } {
    return {
      $priceDetails: computed(() => {
        // logged for acc debugging purposes, will be removed
        return this.priceDetails;
      }),
      $pdpChoices: computed(() => this.pdpChoices),
      $printPositions: computed(() => this.printPositions),
      $sapCustomerId: computed(() => this.sapCustomerId),
      $isEditMode: computed(() => this.isEditMode),
      $personalization: computed(() => this.personalization),
      $isDirectApproval: computed(() => this.isDirectApproval),
      $isFirstTimeOpened: computed(() => this.isFirstTimeOpened),
      $isOrderDisabled: computed(() => this.isOrderDisabled),
      $isConceptDisabled: computed(() => this.isConceptDisabled),
      $customerCompanyLogoUrl: computed(() => this.customerCompanyLogoUrl),
    };
  },

  data() {
    return {
      pinturaEditorTop: '0px',
      DoSmallDetailsPart,
      myOptions: {
        labels: {
          buttonSkip: this.$t('main.wizard-tour-skip'),
          buttonPrevious: this.$t('main.wizard-tour-previous'),
          buttonNext: this.$t('main.wizard-tour-next'),
          buttonStop: this.$t('main.wizard-tour-finish'),
        },
      },
      steps: [] as WizardTourStep[],
      uploadedFile: null as unknown as File,
      base64UploadedFileData: '',
      debounce,
      isConcept: false,

      util: 'finetune',
      // Pintura model
      pinturaShapes: {
        imageAnnotation: [
          /**
           * Annotation shape defining print position area
           */
        ] as AnnotationShape[],
      },
      productPrintData: {} as ProductPrintData,
      pinturaPreviewImage: null as null | string,

      isModalOpen: false,

      isProductImageLoaded: false,
      isPinturaExplicitDisableFlag: false,

      isBellNotification: true,
      isBellClicked: false,

      personalization: {} as Personalization,
      triggerSmallDetailsCheck: false,
      isTourStarted: false,
      zoomLevel: 1,
      isVerticalRulerDisplayed: false,
      isHorizontalRulerDisplayed: false,
    };
  },

  computed: {
    ...mapStores(
      useShapeStore,
      useProductStore,
      useSectionStore,
      useNotificationStore,
      useHistoryStore,
      useMirrorStore
    ),
    ...mapState(useNotificationStore, [
      'allValidatorPipelineResults',
      'notificationsForBell',
      'redWarrnings',
    ]),
    ...mapState(useShapeStore, [
      'shapes',
      'positionShapeMap',
      'isLoaderActive',
      'activeShapeId',
    ]),
    ...mapState(useColorsManagementStore, ['availablePmsColors']),
    ...mapState(useProductStore, [
      'activePosition',
      'area',
      'rotation',
      'unavailablePrintPositions',
      'selectedPrintPosition',
      'selectedVariant',
      'positionsSetFromProps',
      'data',
      'isProductPrintable',
      'editedPositions',
      'isLaserConverter',
      'isDebossingTechnique',
      'isPantoneColorTechnique',
      'isSmallDetailsTechnique',
      'isProcessingOrder',
      'previousManagedPrintPositionId',
      'getDebossingColor',
    ]),
    ...mapState(useMirrorStore, ['mirrorShapes', 'positionMirrorShapeMap']),
    ...mapState(useShapeBoxSectionStore, ['getIsValidInput']),
    ...mapState(useLangStore, ['languageInUse']),

    isDisabledCom(): boolean {
      // Check to see if there are any red notifications
      const redNotification = this.notificationsForBell.some(
        (n) => n.validatorColor === 'Red' && n.validatorName !== 'smallDetails'
      );

      return (
        this.unavailablePrintPositions.length === 0 ||
        this.shapes.length === 0 ||
        redNotification ||
        !this.getIsValidInput
      );
    },

    isManualCheckVisible(): boolean {
      const usedTechniques = this.unavailablePrintPositions.map((x) => {
        return this.data.productPrintPositions
          .find((p) => p.name === x.positionName)
          ?.printTechniques.find(
            (t) => t.printTechnique.code === x.technique.printTechniqueSAPCode
          );
      });
      return usedTechniques.some((x) => x?.printTechnique.limitation !== null);
    },

    activePositionShapes(): unknown[] {
      if (!this.isProductImageLoaded) {
        return [];
      }

      const shapes = [
        ...this.shapes.filter((shape) =>
          this.positionShapeMap.some(
            (psm) =>
              psm.id === this.selectedPrintPosition.id &&
              psm.logoId === shape.id
          )
        ),
      ];

      const mirror = [
        ...this.mirrorShapes.filter((s) =>
          this.positionMirrorShapeMap.some(
            (psm) =>
              psm.positionId === this.selectedPrintPosition.id &&
              psm.mirrorId === s.id
          )
        ),
      ];

      return [...shapes, ...mirror];
    },

    isEditMode(): boolean {
      return this.personalizationId !== '';
    },

    productName(): string {
      return this.data && this.data.name;
    },

    isPinturaDisabled(): boolean {
      return (
        this.isModalOpen ||
        this.unavailablePrintPositions.length === 0 ||
        this.isPinturaExplicitDisableFlag ||
        !this.isProductPrintable
      );
    },

    // NOTE: This will add the PRINT AREA into the annotation array of Pintura
    //      Set the PRINT AREA as an immutable shape in the annotation array (Branko sent a link on Skype)
    // NOTE: We should also add the code which will add the other LOGOS from the SHAPE STORE here, so they get added into the annotation array of Pintura
    productPositionAnnotationObject(): any {
      if (this.unavailablePrintPositions.length) {
        if (this.activePosition.printArea?.shapeType === 'Ellipse') {
          return [
            {
              id: this.selectedPrintPosition.id,
              ...this.drawPoints(),
              strokeColor: colorStringToColorArray('rgba(247, 202, 24, 1)'),
              strokeWidth: `${lineWidth}%`,
              rotation: this.rotation,
            },
          ];
        }

        if (this.activePosition.printArea?.shapeType === 'Rectangle') {
          const annotationShapes = [];

          if (this.activePosition.category === Category.RoundItem) {
            const originalImageWidth =
              (this.activePosition.originalImageWidthPx *
                this.activePosition.originalImageWebDpi) /
              this.activePosition.originalImageDpi;
            const categoryConfig = this.activePosition
              .categoryConfiguration as RoundItemConfiguration;
            const panelWidth = mm2px(
              categoryConfig.productDiameter,
              this.activePosition!.originalImageWebDpi || 96
            );

            const centeredPanelX = (originalImageWidth - panelWidth) / 2;

            const { printArea } = this.activePosition;
            const points: ProductPrintAreaPoint[] = (
              printArea as ProductPrintArea
            )?.productPrintAreaPoints.sort((a, b) =>
              a.sequenceNo > b.sequenceNo ? 1 : -1
            );

            if (!points) return [];

            const xPositionOfPrintPosition =
              (this.activePosition.printArea &&
                mm2px(
                  points[0].distanceFromLeftEdge,
                  this.activePosition!.originalImageWebDpi || 96
                )) ||
              0;

            this.setPanelPosition({
              width:
                centeredPanelX +
                panelWidth / categoryConfig.numberOfPanels -
                this.area(this.activePosition.id)[0].x,
              x: xPositionOfPrintPosition,
            });
            const scaleRatio =
              (this.activePosition.originalImageWebDpi as number) /
              (this.activePosition.originalImageDpi as number);
            let printPositionHeight =
              (this.activePosition.originalImageHeightPx as number) *
              scaleRatio;

            for (
              let index = 0;
              index < categoryConfig.numberOfPanels - 1;
              index++
            ) {
              annotationShapes.push(
                ...drawPanelLines(
                  printPositionHeight,
                  centeredPanelX +
                    (panelWidth * (index + 1)) / categoryConfig.numberOfPanels
                )
              );
            }
          }
          return [
            {
              id: this.selectedPrintPosition.id,
              x: this.drawPoints().x,
              y: this.drawPoints().y,
              width: this.drawPoints().w,
              height: this.drawPoints().h,
              strokeColor: colorStringToColorArray('rgba(247, 202, 24, 1)'),
              strokeWidth: `${lineWidth}%`,
              rotation: this.rotation,
            },
            ...annotationShapes,

            // {
            //   id: this.selectedPrintPosition.id,
            //   x: this.drawPoints().x,
            //   y: this.drawPoints().y + 60,
            //   width: this.drawPoints().w,
            //   height: this.drawPoints().h,
            //   strokeColor: colorStringToColorArray('rgba(247, 202, 24, 1)'),
            //   strokeWidth: `${lineWidth}%`,
            //   rotation: this.rotation,
            // },
          ];
        }
        return [
          {
            id: this.selectedPrintPosition.id,
            points: this.drawPoints(),
            strokeColor: colorStringToColorArray('rgba(247, 202, 24, 1)'),
            strokeWidth: `${lineWidth}%`,
          },
        ];
      } else {
        return [];
      }
    },

    modalHandlers() {
      return {
        beforeClose: () => {
          this.isModalOpen = false;
        },
      };
    },

    base64EvaluatedBlob(): Blob {
      return base64StringToBlob(this.base64UploadedFileData, 'image/png');
    },
  },

  watch: {
    // Do not touch, if you do, we will know.
    'data.variants'() {
      if (this.data.variants) {
        let selectedVariantColor = '';
        if (this.isEditMode && this.personalization.productVariants) {
          selectedVariantColor =
            this.personalization.productVariants[0].variantColor;
          this.setSelectedVariants(this.personalization.productVariants);
        }
        if (!this.isEditMode) {
          selectedVariantColor =
            this.pdpChoices.productVariants[0].variantColor;
          this.setSelectedVariants(this.pdpChoices.productVariants);
        }

        const variant = this.data.variants?.find(
          (varImg) => varImg.colour === selectedVariantColor
        ) as ProductPrintVariant;

        this.setSelectedVariant(variant);
        this.pinturaPreviewImage = this.getPreviewImage();
      }
    },

    'selectedPrintPosition.name'() {
      this.pinturaPreviewImage = this.getPreviewImage();
    },

    // isFirstTimeOpened() {
    //   console.log("isFirstTimeOpened watcher, 'this' is:", this);
    //   if (this.isFirstTimeOpened) {
    //     var importWizard = document.querySelector('w2p-import-wizard');
    //     if (importWizard?.shadowRoot) {
    //       var tourElement =
    //         importWizard?.shadowRoot.querySelector('#wizardTour');
    //       console.log(
    //         "isFirstTimeOpened watcher, 'tourElement' is:",
    //         tourElement
    //       );
    //       (tourElement as any).__vue__.$tours['wizardTour'].start();
    //     } else {
    //       (this as any).$tours['wizardTour'].start();
    //     }
    //   }
    // },

    async languageInUse() {
      await this.setDefaultTranslations();

      if (process.env.VUE_APP_USE_LOCAL_TRANSLATIONS === 'false') {
        var translationsJson = await customersAPI.getTranslations(
          this.languageInUse
        );
        this.$i18n.setLocaleMessage(this.languageInUse, translationsJson.data);
      }

      this.$i18n.locale = this.languageInUse;

      this.initializeWizardTourSteps();
    },

    selectedVariant() {
      this.pinturaPreviewImage = this.getPreviewImage();
    },

    activePositionShapes(n, o) {
      this.sectionStore.$patch((state) => {
        const methodName = n.length > 0 ? 'setAppContext' : 'removeAppContext';
        state.appContext = this[methodName](
          state.appContext,
          'shape-manipulation'
        );
      });
    },

    triggerSmallDetails(newValue) {
      this.triggerSmallDetailsCheck = newValue;
    },

    async pdpChoices(newValue) {
      console.log('PDP');
      try {
        if (newValue !== undefined) {
          await this.getProduct(this.pdpChoices.masterProductSKU);

          if (!this.isEditMode) {
            await this.updateEmptyPrintPositions();
          }

          await this.getAvailablePmsColors();

          if (this.logoOnlyArtwork.length > 0) {
            for (
              let i = 0;
              i < this.pdpChoices.productPrintPositions.length;
              i++
            ) {
              const position = this.pdpChoices.productPrintPositions[i];
              const currentPosition = this.productStore.printPositions.find(
                (pos) => pos.name === position.printPositionSAPCode
              );

              const currentTechnique = currentPosition?.printTechniques.find(
                (tech) =>
                  tech.printTechnique.code === position.printTechniqueSAPCode
              );

              const blob = base64StringToBlob(
                this.logoOnlyArtwork[i].pngFile,
                'image/png'
              );
              if (currentPosition && currentTechnique) {
                await this.useArtwork(
                  {
                    ...this.logoOnlyArtwork[i],
                    pngFile: blob,
                    pngFileWithoutConverters: blob,
                    processed: '',
                    customerId: '',
                    thumbnailUrl: '',
                    variantArtworkImages: [],
                  } as ArtworkPayload,
                  i === 0,
                  currentPosition.id,
                  currentTechnique.id,
                  true
                );

                const limitation = currentTechnique.printTechnique.limitation;
                if (limitation) {
                  this.shapes[i].smallDetailsResult = {
                    ...DefaultSmallDetailsResult,
                    isCheckValid: false,
                  };
                }
              }
            }

            const eventPayload = generateEventPayload();

            this.setActiveShapeId(this.logoOnlyArtwork[0].id);

            this.$emit('wizard:price-params-changed', {
              productPrintPositions: eventPayload,
            });
          }

          this.initializeMirrorSetting();

          this.setCommercialRemarkNotification();

          const snapshot = createStateSnapshot();
          this.recordHistory(snapshot);
        }
      } catch (e: unknown) {
        console.error((e as Error).message);
      }
    },

    async personalizationId(newValue) {
      try {
        /*
        In case that wizzard needs to be initialized with data from
        previously saved personalization, first step is to get personalization
        from backend.
      */
        if (newValue !== undefined && newValue !== '') {
          if (this.isEditMode) {
            const { data } = await customersAPI.getPersonalization(
              this.personalizationId
            );
            this.personalization = data;
          }
          /*
        In both cases, next step is to retrieve product data and list of
        avaible pms colors. For edit mode, personalization object is data
        source and for regular flow pdpChoices.
      */
          const productSKU = this.isEditMode
            ? this.personalization.productSKU
            : this.pdpChoices.masterProductSKU;
          let product: ProductPrintData;
          if (productSKU !== '') {
            product = await this.getProduct(productSKU);
          }

          /*
        Edit flow: initialize print positions and active
        print position. Print postion object needs to be mapped
        to shape that is used in store.
      */
          if (this.isEditMode) {
            const printPositionsData =
              this.personalization.proofs[0].productPrintPositions;
            const printPostionAddModel: ProductPrintPositionAddModel[] =
              printPositionsData.map((pos) => {
                const position = product.productPrintPositions.find(
                  (p) => p.name === pos.sapCode
                );
                const techniqueId = position?.printTechniques.find(
                  (t) =>
                    t.printTechnique.code === pos.productPrintTechnique.sapCode
                )?.id;
                const technique = position?.printTechniques.find(
                  (t) =>
                    t.printTechnique.code === pos.productPrintTechnique.sapCode
                );
                return {
                  positionId: position?.id,
                  positionName: pos.name,
                  printPositionSAPCode: position?.name,
                  technique: {
                    id: techniqueId,
                    printTechniqueSAPCode: pos.productPrintTechnique.sapCode,
                    techniqueName: pos.productPrintTechnique.name,
                    groupCode: technique?.printTechnique?.group?.code,
                  },
                } as ProductPrintPositionAddModel;
              });

            /*
         initializeStateFromPersonalization action from shape store is used
         to initialize all arrays and data needed for working on wizard: shapes,
         artworkPayloads, positionShapeMap
        */
            await this.initializeStateFromPersonalization(this.personalization);
            this.setUnavailablePrintPositions(printPostionAddModel);

            // Revalidate unavailable print positions
            this.revalidateUnavailablePrintPositions();

            if (DoSmallDetailsPart) {
              this.initializeSmallDetailsNotifications();
            }

            this.initializeMirrorPositions();
          }

          await this.getAvailablePmsColors();

          if (this.personalization) {
            if (this.data.variants) {
              const selectedVariantColor = this.isEditMode
                ? this.personalization.productVariants[0].variantColor
                : this.pdpChoices.productVariants[0].variantColor;

              const variant = this.data.variants?.find(
                (varImg) => varImg.colour == selectedVariantColor
              ) as ProductPrintVariant;

              this.setSelectedVariant(variant);
              this.setSelectedVariants(this.personalization.productVariants);
              this.pinturaPreviewImage = this.getPreviewImage();
            }
          }

          if (this.isEditMode) {
            this.initializeMirrorSettingsFromProof(
              this.personalization.proofs[0]
            );
          } else {
            this.initializeMirrorSetting();
          }

          this.setCommercialRemarkNotification();

          const snapshot = createStateSnapshot();
          this.recordHistory(snapshot);
        }
      } catch (e: unknown) {
        console.error(e);
      }
    },

    isHorizontalRulerDisplayed() {
      if (this.isHorizontalRulerDisplayed) {
        this.pinturaEditorTop = '40px';
      } else {
        this.pinturaEditorTop = '0px';
      }
    },
    async personalizationJson(newVal) {
      this.resetColorsStateToDefault();
      this.resetShapeStateToDefault();
      this.resetProductStateToDefault();
      this.resetPriceStateToDefault();
      this.resetNotificationStoreStateToDefault();

      const productSKU = newVal.form.productSKU;
      let product: ProductPrintData;
      if (productSKU !== '') {
        product = await this.getProduct(productSKU);
      }
      const printPositionsData =
        this.personalizationJson.form.newProofForm.newProductPrintPositionForms;
      const printPostionAddModel: ProductPrintPositionAddModel[] =
        printPositionsData.map((pos: NewProductPrintPositionForms) => {
          const position = product.productPrintPositions.find(
            (p) => p.name === pos.SAPCode
          );
          const techniqueId = position?.printTechniques.find(
            (t) => t.printTechnique.code === pos.productPrintTechniqueSAPCode
          )?.id;
          const technique = position?.printTechniques.find(
            (t) => t.printTechnique.code === pos.productPrintTechniqueSAPCode
          );
          return {
            positionId: position?.id,
            positionName: pos.name,
            printPositionSAPCode: position?.name,
            technique: {
              id: techniqueId,
              printTechniqueSAPCode: pos.productPrintTechniqueSAPCode,
              techniqueName: pos.productPrintTechniqueSAPCode,
              groupCode: technique?.printTechnique?.group?.code,
            },
          } as ProductPrintPositionAddModel;
        });

      /*
         initializeStateFromPersonalization action from shape store is used
         to initialize all arrays and data needed for working on wizard: shapes,
         artworkPayloads, positionShapeMap
        */
      await this.initializeStateFromPersonalizationJson(newVal);
      this.setUnavailablePrintPositions(printPostionAddModel);

      // Revalidate unavailable print positions
      this.revalidateUnavailablePrintPositions();
    },
  },

  updated() {
    if (this.isFirstTimeOpened && !this.isTourStarted) {
      var importWizard = document.querySelector('w2p-import-wizard-wrapper');
      var importWizardOld = document.querySelector('w2p-import-wizard');
      if (importWizard?.shadowRoot) {
        var tourElement = importWizard?.shadowRoot.querySelector('#wizardTour');
        if (tourElement) {
          this.isTourStarted = true;
          (tourElement as any).__vue__.$tours['wizardTour'].start();
        }
      } else if (importWizardOld?.shadowRoot) {
        var tourElementOld =
          importWizardOld?.shadowRoot.querySelector('#wizardTour');
        if (tourElementOld) {
          this.isTourStarted = true;
          (tourElementOld as any).__vue__.$tours['wizardTour'].start();
        }
      } else {
        this.isTourStarted = true;
        (this as any).$tours['wizardTour'].start();
      }
    }
  },

  async mounted() {
    /*
     * Attach subscribers
     */
    attachProductActionSubscriber();
    attachShapeActionSubscriber();
    attachUserActionSubscriber();

    this.initializeWizardTourSteps();

    try {
      /*
        In case that wizzard needs to be initialized with data from
        previously saved personalization, first step is to get personalization
        from backend.
      */
      if (this.isEditMode) {
        const { data } = await customersAPI.getPersonalization(
          this.personalizationId
        );
        this.personalization = data;
      }
      /*
        In both cases, next step is to retrieve product data and list of
        avaible pms colors. For edit mode, personalization object is data
        source and for regular flow pdpChoices.
      */
      const productSKU = this.isEditMode
        ? this.personalization.productSKU
        : this.pdpChoices.masterProductSKU;
      let product: ProductPrintData;
      if (productSKU !== '') {
        product = await this.getProduct(productSKU);
      }

      /*
        Regular flow: initialize print positions and active
        print position.
      */
      if (!this.isEditMode) {
        await this.updateEmptyPrintPositions();
      }

      /*
        Edit flow: initialize print positions and active
        print position. Print postion object needs to be mapped
        to shape that is used in store.
      */
      if (this.isEditMode) {
        const printPositionsData =
          this.personalization.proofs[0].productPrintPositions;
        const printPostionAddModel: ProductPrintPositionAddModel[] =
          printPositionsData.map((pos) => {
            const position = product.productPrintPositions.find(
              (p) => p.name === pos.sapCode
            );
            const techniqueId = position?.printTechniques.find(
              (t) => t.printTechnique.code === pos.productPrintTechnique.sapCode
            )?.id;
            const technique = position?.printTechniques.find(
              (t) => t.printTechnique.code === pos.productPrintTechnique.sapCode
            );
            return {
              positionId: position?.id,
              positionName: pos.name,
              printPositionSAPCode: position?.name,
              technique: {
                id: techniqueId,
                printTechniqueSAPCode: pos.productPrintTechnique.sapCode,
                techniqueName: pos.productPrintTechnique.name,
                groupCode: technique?.printTechnique?.group?.code,
              },
            } as ProductPrintPositionAddModel;
          });

        /*
         initializeStateFromPersonalization action from shape store is used
         to initialize all arrays and data needed for working on wizard: shapes,
         artworkPayloads, positionShapeMap
        */
        await this.initializeStateFromPersonalization(this.personalization);
        this.setUnavailablePrintPositions(printPostionAddModel);

        // Revalidate unavailable print positions
        this.revalidateUnavailablePrintPositions();

        if (DoSmallDetailsPart) {
          this.initializeSmallDetailsNotifications();
        }

        this.initializeMirrorPositions();
      }

      await this.getAvailablePmsColors();

      if (this.personalization) {
        if (this.data.variants) {
          const selectedVariantColor = this.isEditMode
            ? this.personalization.productVariants[0].variantColor
            : this.pdpChoices.productVariants[0].variantColor;

          const variant = this.data.variants?.find(
            (varImg) => varImg.colour == selectedVariantColor
          ) as ProductPrintVariant;

          this.setSelectedVariant(variant);
          this.setSelectedVariants(this.personalization.productVariants);
          this.pinturaPreviewImage = this.getPreviewImage();
        }
      }

      // if (this.logoOnlyArtwork.length > 0) {
      //   for (let i = 0; i < this.pdpChoices.productPrintPositions.length; i++) {
      //     const position = this.pdpChoices.productPrintPositions[i];
      //     const currentPosition = this.productStore.printPositions.find(
      //       (pos) => pos.name === position.printPositionSAPCode
      //     );

      //     const currentTechnique = currentPosition?.printTechniques.find(
      //       (tech) =>
      //         tech.printTechnique.code === position.printTechniqueSAPCode
      //     );

      //     const blob = base64StringToBlob(
      //       this.logoOnlyArtwork[i].pngFile,
      //       'image/png'
      //     );
      //     if (currentPosition && currentTechnique) {
      //       await this.useArtwork(
      //         {
      //           ...this.logoOnlyArtwork[i],
      //           pngFile: blob,
      //           pngFileWithoutConverters: blob,
      //           processed: '',
      //           customerId: '',
      //           thumbnailUrl: '',
      //           variantArtworkImages: [],
      //         } as ArtworkPayload,
      //         i === 0,
      //         currentPosition.id,
      //         currentTechnique.id,
      //         true
      //       );

      //       const limitation = currentTechnique.printTechnique.limitation;
      //       if (limitation) {
      //         this.shapes[i].smallDetailsResult = {
      //           ...DefaultSmallDetailsResult,
      //           isCheckValid: false,
      //         };
      //       }
      //     }
      //   }

      //   const eventPayload = generateEventPayload();

      //   this.setActiveShapeId(this.logoOnlyArtwork[0].id);

      //   this.$emit('wizard:price-params-changed', {
      //     productPrintPositions: eventPayload,
      //   });
      // }

      if (!this.isEditMode) {
        this.setSelectedVariants(this.pdpChoices.productVariants);
      }

      if (this.isEditMode) {
        this.initializeMirrorSettingsFromProof(this.personalization.proofs[0]);
      } else {
        this.initializeMirrorSetting();
      }

      if (productSKU !== '') {
        product = await this.getProduct(productSKU);
      }

      /*
         initializeStateFromPersonalization action from shape store is used
         to initialize all arrays and data needed for working on wizard: shapes,
         artworkPayloads, positionShapeMap
        */

      this.setCommercialRemarkNotification();

      window.constructPersonalizationJSON = this.constructPersonalizationJSON;

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    } catch (e: unknown) {
      console.error(e);
    }
  },

  methods: {
    ...mapActions(useShapeStore, [
      'setPanelPosition',
      'resetShapeStateToDefault',
    ]),
    ...mapActions(useNotificationStore, [
      'setCommercialRemarkNotification',
      'resetNotificationStoreStateToDefault',
    ]),
    ...mapActions(useLangStore, ['setDefaultTranslations']),
    ...mapActions(useProductStore, ['resetProductStateToDefault']),
    ...mapActions(useColorsManagementStore, ['resetColorsStateToDefault']),
    ...mapActions(usePriceSectionStore, ['resetPriceStateToDefault']),

    async recalculateArtworkPosition() {
      const positionId = this.activePosition.id as string;
      if (this.previousManagedPrintPositionId !== positionId) {
        this.shapes.forEach((payload) => {
          if (
            this.positionShapeMap.find(
              (element) =>
                element.id === positionId &&
                payload.kind === 'Logo' &&
                payload.id === element.logoId
            )
          ) {
            this.removeShape(payload as AnnotationShape, false);
            var newId = uuidv4();
            this.useArtwork(
              {
                ...payload,
                id: newId,
                personalizationArtworkId: newId,
              } as ArtworkPayload,
              true,
              positionId,
              this.selectedPrintPosition.techniqueId as string,
              false
            );
          }
        });

        this.shapes
          .filter((s) => s.kind === 'Text')
          .forEach((shape) => {
            if (
              this.positionShapeMap.find(
                (element) =>
                  element.id === positionId && shape.id === element.logoId
              )
            ) {
              positionText(shape as TextShape, positionId, true);
            }
          });
      }
    },
    initializeWizardTourSteps() {
      this.myOptions = {
        labels: {
          buttonSkip: this.$t('main.wizard-tour-skip'),
          buttonPrevious: this.$t('main.wizard-tour-previous'),
          buttonNext: this.$t('main.wizard-tour-next'),
          buttonStop: this.$t('main.wizard-tour-finish'),
        },
      };

      this.steps = [
        {
          target: '#logo-library', // We're using document.querySelector() under the hood
          header: {
            title: this.$t('main.wizard-tour-step-1-header').toString(),
          },
          content: this.$t('main.wizard-tour-step-1-content').toString(),
          offset: -200,
          params: {
            placement: 'right', // Any valid Popper.js placement. See https://popper.js.org/popper-documentation.html#Popper.placements
            index: 1,
            totalSteps: 5,
          },
        },
        {
          target: '#print-positions',
          header: {
            title: this.$t('main.wizard-tour-step-2-header').toString(),
          },
          content: this.$t('main.wizard-tour-step-2-content').toString(),
          offset: -200,
          params: {
            placement: 'bottom', // Any valid Popper.js placement. See https://popper.js.org/popper-documentation.html#Popper.placements
            index: 2,
            totalSteps: 5,
          },
        },
        {
          target: '#artwork-details-panel',
          header: {
            title: this.$t('main.wizard-tour-step-3-header').toString(),
          },
          content: this.$t('main.wizard-tour-step-3-content').toString(),
          params: {
            placement: 'left', // Any valid Popper.js placement. See https://popper.js.org/popper-documentation.html#Popper.placements
            index: 3,
            totalSteps: 5,
          },
        },
        {
          target: '#show-order-details',
          header: {
            title: this.$t('main.wizard-tour-step-4-header').toString(),
          },
          content: this.$t('main.wizard-tour-step-4-content').toString(),
          params: {
            placement: 'top', // Any valid Popper.js placement. See https://popper.js.org/popper-documentation.html#Popper.placements
            index: 4,
            totalSteps: 5,
          },
        },
        {
          target: '#save-and-order',
          header: {
            title: this.$t('main.wizard-tour-step-5-header').toString(),
          },
          content: this.$t('main.wizard-tour-step-5-content').toString(),
          params: {
            placement: 'left', // Any valid Popper.js placement. See https://popper.js.org/popper-documentation.html#Popper.placements
            index: 5,
            totalSteps: 5,
          },
        },
      ];
    },
    ...mapActions(useMirrorStore, [
      'updateMirrorShape',
      'initializeMirrorSetting',
      'initializeMirrorPositions',
      'initializeMirrorSettingsFromProof',
    ]),
    ...mapActions(useHistoryStore, ['recordHistory']),
    ...mapActions(useNotificationStore, [
      'resetAllValidatorPipelineResults',
      'setAllValidatorPipelineResults',
      'resetNotificationsForOneShape',
      'revalidateUnavailablePrintPositions',
      'revalidateResolutionOnly',
      'initializeSmallDetailsNotifications',
      'revalidateColorsOnly',
      'revalidateEmptyPrintPositions',
    ]),
    ...mapActions(useColorsManagementStore, [
      'getAvailablePmsColors',
      'changeToClosestPantonesFromPalette',
    ]),
    ...mapActions(useShapeStore, [
      'addArtworkPayload',
      'removeTextPayload',
      'initializeStateFromPersonalization',
      'initializeStateFromPersonalizationJson',
      'addTextPayload',
      'deselectShape',
      'selectShape',
      'applyLaserConverter',
      'applyDebossingConverter',
      'applyPantoneConverter',
      'convertToBlob',
      'removeArtworkPayload',
    ]),
    ...mapActions(useProductStore, [
      'getProduct',
      'setActivePrintPosition',
      'setUnavailablePrintPositions',
      'setSelectedVariant',
      'setSelectedVariants',
      'getVariantLaserColor',
      'drawPoints',
      'polygonDetails',
    ]),
    setAppContext,
    removeAppContext,
    log: window.console.log,
    onBackButtonClick() {
      this.$emit('wizard:back');
    },
    emitEvent() {
      this.$emit('preview:pdf');
    },

    resetFlag() {
      this.triggerSmallDetailsCheck = false;
      this.$emit('action:trigger-small-details');
    },
    emitMoreDetails() {
      console.log('EMIT: action:print-techniques-see-more-details');
      this.$emit('action:print-techniques-see-more-details');
    },
    emitParamsChanged(event: any) {
      console.log('EMIT: wizard:price-params-changed');
      this.$emit('wizard:price-params-changed', event);
    },
    getPreviewImage() {
      return this.selectedVariant !== undefined
        ? (this.selectedVariant.images?.find(
            (i) => i.printPositionID === this.selectedPrintPosition.id
          )?.imageWebUrl as string)
        : null;
    },
    async onEditPrintPosition(
      event: ProductPrintPositionAddModel[],
      newPositionId: string,
      oldPositionId: string
    ) {
      const isLaserChoosen = this.isLaserConverter(newPositionId);
      const isDebossingChoosen = this.isDebossingTechnique(newPositionId);
      const isPantoneChoosen = this.isPantoneColorTechnique(newPositionId);
      const techniqueCode = this.unavailablePrintPositions.find(
        (pp) => pp.positionId === newPositionId
      )?.technique.printTechniqueSAPCode;
      const isSmallDetailsTech = this.isSmallDetailsTechnique(
        newPositionId,
        techniqueCode
      );

      const printPositionShapes = this.shapes.filter((shape) =>
        this.positionShapeMap.some(
          (psm) => psm.id === newPositionId && psm.logoId === shape.id
        )
      );

      const textPrintPositionShapes = this.shapes
        .filter((shape) =>
          this.positionShapeMap.some(
            (psm) => psm.id === newPositionId && psm.logoId === shape.id
          )
        )
        .filter(textShapes) as TextShape[];

      this.shapeStore.$patch((state) => {
        printPositionShapes.map((x) => {
          const shapeIdx = state.shapes.findIndex((p) => p.id === x.id);

          const smallDetails = mapSmallDetailsPayload(x, isSmallDetailsTech);

          state.shapes.splice(shapeIdx, 1, {
            ...state.shapes[shapeIdx],
            smallDetailsResult: smallDetails,
          });
        });

        textPrintPositionShapes.map((x) => {
          const textIdx = state.shapes.findIndex((p) => p.id === x.id);

          positionText(
            state.shapes[textIdx] as TextShape,
            this.productStore.selectedPrintPosition.id,
            true
          );

          const smallDetails = mapSmallDetailsPayload(x, isSmallDetailsTech);

          state.shapes.splice(textIdx, 1, {
            ...state.shapes[textIdx],
            smallDetailsResult: smallDetails,
          });
        });
      });

      if (isLaserChoosen) {
        const choosenPosition = event.find(
          (x) => x.positionId === newPositionId
        );

        const configObj = this.getVariantLaserColor(
          choosenPosition?.positionId as string,
          choosenPosition?.technique?.id as string,
          this.selectedVariant.sku
        );

        const { red: r, green: g, blue: b } = configObj.laserColor;
        const color = [r, g, b];

        textPrintPositionShapes.forEach((s) => {
          const idx = this.shapes.findIndex((shape) => shape.id === s.id);

          this.shapeStore.$patch((state) => {
            state.shapes.splice(idx, 1, {
              ...s,
              w2pText: { ...s.w2pText, color: color },
            });
          });
        });

        for (var s of printPositionShapes) {
          await this.applyLaserConverter(
            s.id,
            choosenPosition?.positionId as string,
            choosenPosition?.technique?.id as string
          );
        }
      } else if (isPantoneChoosen) {
        for (var d of printPositionShapes) {
          await this.applyPantoneConverter(d.id);
        }
        textPrintPositionShapes.forEach((s) => {
          const idx = this.shapes.findIndex((shape) => shape.id === s.id);
          const foundColor = this.availablePmsColors.find(
            (pms) => pms.name === s.w2pText.colorName
          ) as PmsColor;

          const { red: r, green: g, blue: b } = foundColor;
          const color = [r, g, b];
          this.shapeStore.$patch((state) => {
            const current = this.shapes[idx];
            if (current.kind === 'Text') {
              state.shapes.splice(idx, 1, {
                ...current,
                w2pText: { ...s.w2pText, color: color },
                pngFile: s.pngFileWithoutConverters,
                variantArtworkImages: [],
                backgroundImage: s.pngFileWithoutConverters
                  ? URL.createObjectURL(s.pngFileWithoutConverters)
                  : '',
              });
            }
          });

          this.updateMirrorShape(this.shapes[idx]);
        });
      } else if (isDebossingChoosen) {
        const debossingColor = this.getDebossingColor;

        if (debossingColor) {
          textPrintPositionShapes.forEach((s) => {
            const idx = this.shapes.findIndex((shape) => shape.id === s.id);

            this.shapeStore.$patch((state) => {
              state.shapes.splice(idx, 1, {
                ...s,
                w2pText: {
                  ...s.w2pText,
                  color: debossingColor,
                },
              });
            });
          });
        }

        for (var sh of printPositionShapes) {
          await this.applyDebossingConverter(sh.id);
        }
      } else {
        printPositionShapes.forEach(async (s) => {
          const artworkIndex = this.shapes.findIndex(
            (artwork) => artwork.id === s.id
          );

          const shapePayload = { ...this.shapes[artworkIndex] };

          if (shapePayload.kind === 'Logo') {
            this.shapeStore.$patch((state) => {
              state.shapes.splice(artworkIndex, 1, {
                ...shapePayload,
                pngFile: shapePayload.pngFileWithoutConverters,
                variantArtworkImages: [],
                backgroundImage: shapePayload.pngFileWithoutConverters
                  ? URL.createObjectURL(shapePayload.pngFileWithoutConverters)
                  : '',
              });
            });
            this.updateMirrorShape(shapePayload);
          }
        });

        textPrintPositionShapes.forEach((s) => {
          const idx = this.shapes.findIndex((shape) => shape.id === s.id);
          const foundColor = this.availablePmsColors.find(
            (pms) => pms.name === s.w2pText.colorName
          ) as PmsColor;

          const { red: r, green: g, blue: b } = foundColor;
          const color = [r, g, b];
          this.shapeStore.$patch((state) => {
            const current = this.shapes[idx];
            if (current.kind === 'Text') {
              state.shapes.splice(idx, 1, {
                ...current,
                w2pText: { ...s.w2pText, color: color },
                pngFile: s.pngFileWithoutConverters,
                variantArtworkImages: [],
                backgroundImage: s.pngFileWithoutConverters
                  ? URL.createObjectURL(s.pngFileWithoutConverters)
                  : '',
              });
            }
          });

          this.updateMirrorShape(this.shapes[idx]);
        });
      }

      await this.recalculateArtworkPosition();

      this.revalidateArtworks(event, newPositionId, oldPositionId);

      this.notificationStore.resetCommercialRemarksNotificationsForBell(
        oldPositionId
      );

      await this.getAvailablePmsColors();

      this.notificationStore.setCommercialRemarkNotification();

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);
    },
    clickedBell() {
      this.isBellClicked = !this.isBellClicked;
    },
    onBellClickAway() {
      this.isBellClicked = false;
    },
    async updateEmptyPrintPositions() {
      let printPositionsFromPdp = [] as ProductPrintPositionAddModel[];

      this.pdpChoices.productPrintPositions.map((position) => {
        let positionToAdd = {} as ProductPrintPositionAddModel;

        if (this.data.productPrintPositions) {
          let filteredPosition = this.data.productPrintPositions.find((pos) => {
            return position.printPositionSAPCode === pos.name;
          });

          if (filteredPosition) {
            let filteredTechnique = filteredPosition.printTechniques.find(
              (tech) => {
                return (
                  tech.printTechnique.code === position.printTechniqueSAPCode
                );
              }
            );

            positionToAdd.positionId = filteredPosition.id;
            positionToAdd.positionName = filteredPosition.name;
            positionToAdd.printPositionSAPCode = filteredPosition.name;
            if (filteredTechnique) {
              positionToAdd.technique = {
                id: filteredTechnique.id,
                printTechniqueSAPCode: filteredTechnique.printTechnique.code,
                techniqueName: filteredTechnique.printTechnique.name,
                groupCode: filteredTechnique.printTechnique.group.code,
              };
            }
          }

          printPositionsFromPdp.push(positionToAdd);
        }
      });
      this.setUnavailablePrintPositions(printPositionsFromPdp);
      this.productStore.$patch({
        positionsSetFromProps: true,
      });
    },
    /**
     * TODO Make generic error handler in Pinia state, and use that one instead
     */
    onLoadError(e: Error) {
      console.error('Give user a notice of loading error...');
      console.error(e);
    },

    onProductImageLoaded({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      dest,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      size,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      src,
    }: {
      dest: File;
      size: { height: number; width: number };
      src: string;
    }) {
      /**
       * Flag that indicates whether we are able to pass down shapes for this position
       * Make sure we have product image loaded first, as we will get some
       *  strange side-effects otherwise, as canvas will not be ready
       */
      this.isProductImageLoaded = true;
      const elements = document.getElementsByTagName('w2p-import-wizard');
      if (elements.length > 0) {
        let wiz = elements[0] as HTMLElement;
        const style = document.createElement('style');
        style.textContent = `
                        .pintura-editor {
                            --color-background: 244, 244, 244;
                            --color-preview-outline: 255, 255, 255;
                        }
                    `;
        if (wiz.shadowRoot) {
          wiz.shadowRoot.append(style);
        }
      }
    },
    async addToImageDecoration(shape: AnnotationShape) {
      if (shape && shape.kind === 'Logo') {
        await this.addArtworkPayload(
          shape,
          this.selectedPrintPosition.id,
          this.selectedPrintPosition?.techniqueId as string,
          true
        );
      } else {
        // reset text value

        const positionId = this.productStore.selectedPrintPosition.id;
        const printPosition = this.productStore.data.productPrintPositions.find(
          (x) => x.id === positionId
        );
        const techniqueId = this.productStore.unavailablePrintPositions.find(
          (x) => x.positionId === positionId
        )?.technique.id;
        if (printPosition) {
          const limitation = printPosition.printTechniques.find(
            (x) => x.id === techniqueId
          )?.printTechnique.limitation;
          if (limitation) {
            shape.smallDetailsResult = {
              ...DefaultSmallDetailsResult,
              isCheckValid: false,
            };
          }
          let colorManagementStore = useColorsManagementStore();
          if (colorManagementStore.lastPalette !== 'ALL') {
            shape.w2pText.colorName = this.availablePmsColors[0].name;
            shape.w2pText.color = [
              this.availablePmsColors[0].red,
              this.availablePmsColors[0].green,
              this.availablePmsColors[0].blue,
            ];
          }
          await this.addTextPayload({ ...shape, kind: 'Text' }, positionId);

          if (shape.w2pText.text !== '') {
            const snapshot = createStateSnapshot();
            this.recordHistory(snapshot);
          }
        }
      }

      const eventPayload = generateEventPayload();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });
    },

    updateImageDecoration(payload: AnnotationShape[], eventType: string) {
      this.shapeStore.$patch((state) => {
        payload.forEach((override) => {
          const found = state.shapes.findIndex((s) => s.id === override.id);
          if (found !== -1) {
            state.shapes.splice(found, 1, override);
          }
        });
      });

      const eventPayload = generateEventPayload();

      if (eventType !== 'selectshape') {
        this.$emit('wizard:price-params-changed', {
          productPrintPositions: eventPayload,
        });
      }
    },

    async onImportArtwork(payload: ArtworkPayload) {
      await this.useArtwork(
        payload,
        true,
        this.selectedPrintPosition.id,
        this.selectedPrintPosition.techniqueId as string,
        true
      );
      this.setActiveShapeId(payload.id);

      const eventPayload = generateEventPayload();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });
    },

    async useArtwork(
      payload: ArtworkPayload,
      selectShape: boolean,
      positionId: string,
      techniqueId: string,
      createSnapshot: boolean
    ) {
      const artworkSpaceConsumed = 97; // value in percents
      let calcArtworkWidth = 0;
      let calcArtworkHeight = 0;
      let shapeX = 0;
      let shapeY = 0;
      const CORRECTION_FACTOR = 0;
      const productStore = useProductStore();

      const currentPrintPosition = productStore.printPositions?.find((p) => {
        return p.id === positionId;
      });
      if (currentPrintPosition) {
        const { originalImageWebDpi, originalImageWidthPx, originalImageDpi } =
          currentPrintPosition;

        const webImageWidthPx =
          originalImageWidthPx &&
          originalImageWebDpi &&
          originalImageDpi &&
          (originalImageWidthPx * originalImageWebDpi) / originalImageDpi;
        const rectLineWidth = (lineWidth * (webImageWidthPx as number)) / 100;

        const aspectRatioWH = payload.pngWidthPixels / payload.pngHeightPixels;
        const aspectRatioHW = payload.pngHeightPixels / payload.pngWidthPixels;

        if (currentPrintPosition.printArea?.shapeType === 'Rectangle') {
          const areaRect = {
            x: this.drawPoints(positionId).x + rectLineWidth / 2,
            y: this.drawPoints(positionId).y + rectLineWidth / 2,
            w: this.drawPoints(positionId).w - rectLineWidth,
            h: this.drawPoints(positionId).h - rectLineWidth,
          };

          const artworkRatio = payload.pngWidthPixels / payload.pngHeightPixels;
          const areaRatio = areaRect.w / areaRect.h;

          // Artwork is more landscape than area - fit to width
          if (artworkRatio > areaRatio) {
            calcArtworkWidth = areaRect.w - CORRECTION_FACTOR;
            calcArtworkHeight =
              (calcArtworkWidth / payload.pngWidthPixels) *
              payload.pngHeightPixels;
          }
          // Artwork is more portrait than area - fit to height
          else {
            calcArtworkHeight = areaRect.h - CORRECTION_FACTOR;
            calcArtworkWidth =
              (calcArtworkHeight / payload.pngHeightPixels) *
              payload.pngWidthPixels;
          }

          let gapW = areaRect.w - calcArtworkWidth;
          let gapH = areaRect.h - calcArtworkHeight;

          shapeX = areaRect.x + gapW / 2;
          shapeY = areaRect.y + gapH / 2;
        }

        if (currentPrintPosition.printArea?.shapeType === 'Ellipse') {
          const min =
            this.drawPoints(positionId).rx < this.drawPoints(positionId).ry
              ? this.drawPoints(positionId).rx
              : this.drawPoints(positionId).ry;
          let size = 0;
          if (
            Math.abs(
              this.drawPoints(positionId).ry - this.drawPoints(positionId).rx
            ) > 630
          ) {
            size = min * 2;
          } else {
            size = min * 1.6;
          }

          if (aspectRatioWH >= aspectRatioHW) {
            calcArtworkWidth = (size * (artworkSpaceConsumed - 15)) / 100;
            calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
          } else {
            calcArtworkHeight = (size * (artworkSpaceConsumed - 15)) / 100;
            calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
          }

          shapeX = this.drawPoints(positionId).x - calcArtworkWidth / 2;
          shapeY = this.drawPoints(positionId).y - calcArtworkHeight / 2;
        }

        if (currentPrintPosition.printArea?.shapeType === 'Polygon') {
          const size = this.polygonDetails(positionId).minDistance;

          if (aspectRatioWH >= aspectRatioHW) {
            calcArtworkWidth = (size * artworkSpaceConsumed) / 100;
            calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
          } else {
            calcArtworkHeight = (size * artworkSpaceConsumed) / 100;
            calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
          }

          shapeX = this.polygonDetails(positionId).x - calcArtworkWidth / 2;
          shapeY = this.polygonDetails(positionId).y - calcArtworkHeight / 2;
        }

        const recalculatedHDPI = recalculateDPI(
          px2mm(
            calcArtworkWidth,
            currentPrintPosition?.originalImageWebDpi as number
          ),
          payload.originalHorizontalResolutionDPIs,
          payload.originalWidthMM
        );

        const recalculatedVDPI = recalculateDPI(
          px2mm(
            calcArtworkHeight,
            currentPrintPosition?.originalImageWebDpi as number
          ),
          payload.originalVerticalResolutionDPIs,
          payload.originalHeightMM
        );

        const rotationInDegrees = currentPrintPosition?.printArea
          .rotation as number;
        const rotationInRadians = (rotationInDegrees * Math.PI) / 180;

        let shapeToAdd: AnnotationShape = {
          zindex: 0,
          kind: 'Logo',
          isRoundLogo: false,
          id: payload.personalizationArtworkId,
          title: payload.title,
          backgroundImage: URL.createObjectURL(payload.pngFile as Blob),
          pngFileWithoutConverters: payload.pngFileWithoutConverters,
          x: shapeX,
          y: shapeY,
          isInArea: true,
          width: calcArtworkWidth,
          height: calcArtworkHeight,
          aspectRatio: calcArtworkWidth / calcArtworkHeight,
          processed: '',
          isSelected: selectShape,
          rotation: rotationInRadians,
          pngHorizontalResolutionDPIs: Math.round(recalculatedHDPI),
          pngVerticalResolutionDPIs: Math.round(recalculatedVDPI),
          smallDetailsResult: payload.smallDetailsResult,
          customerId: payload.customerId,
          fileName: payload.fileName,
          thumbnailUrl: payload.thumbnailUrl,
          originalFileUrl: payload.originalFileUrl,
          pngFileUrl: payload.pngFileUrl,
          pngWidthPixels: payload.pngWidthPixels,
          pngHeightPixels: payload.pngHeightPixels,
          originalWidthMM: payload.originalWidthMM,
          originalHeightMM: payload.originalHeightMM,
          isVectorFileTrueVectorFile: payload.isVectorFileTrueVectorFile,
          spotColorsInOriginalFile: payload.spotColorsInOriginalFile,
          pmsColorsInPngFile: payload.pmsColorsInPngFile.map((color) => ({
            ...color,
            originalName: color.name,
          })),
          customerArtworkId: payload.customerArtworkId,
          pngFile: payload.pngFile,
          personalizationArtworkId: payload.personalizationArtworkId,
          originalHorizontalResolutionDPIs:
            payload.originalHorizontalResolutionDPIs,
          originalVerticalResolutionDPIs:
            payload.originalVerticalResolutionDPIs,
          variantArtworkImages: payload.variantArtworkImages,
        };
        let colorManagementStore = useColorsManagementStore();
        if (colorManagementStore.lastPalette !== 'ALL') {
          this.changeToClosestPantonesFromPalette(shapeToAdd);
        }

        await this.addArtworkPayload(
          { ...shapeToAdd },
          positionId,
          techniqueId,
          createSnapshot
        );
      }

      this.$modal.hide('logo-library-modal');
    },

    openModal() {
      /**
       * Disable pintura while the logo library modal is open
       */
      this.isModalOpen = true;

      /**
       * Open logo library modal
       */
      this.$modal.show('logo-library-modal', { foo: 'bar' });
    },

    updateShape(current: AnnotationShape) {
      /**
       * UPDATE shape x, y, rotation
       */
      const affectedShapeIndex = this.shapes.findIndex(
        (shape) => shape.id === current.id
      );

      const affectedShape = this.shapes[affectedShapeIndex] as AnnotationShape;

      affectedShape.width = current.width;
      affectedShape.height = current.height;
      affectedShape.x = current.x;
      affectedShape.y = current.y;

      if (affectedShape.kind === 'Logo') {
        const dpi = this.activePosition.originalImageWebDpi || 300;
        const newHDPI = recalculateDPI(
          px2mm(current.width as number, dpi),
          affectedShape.originalHorizontalResolutionDPIs as number,
          affectedShape.originalWidthMM as number
        );

        const newVDPI = recalculateDPI(
          px2mm(current.height as number, dpi),
          affectedShape.originalVerticalResolutionDPIs as number,
          affectedShape.originalHeightMM as number
        );

        affectedShape.pngHorizontalResolutionDPIs = Math.round(newHDPI);
        affectedShape.pngVerticalResolutionDPIs = Math.round(newVDPI);

        this.shapeStore.$patch((state) => {
          if (affectedShape.kind === 'Logo') {
            state.shapes.splice(affectedShapeIndex, 1, {
              ...current,
              ...affectedShape,
            });
          }
        });

        const printPosition = this.data.productPrintPositions.find(
          (p) => p.id === this.selectedPrintPosition.id
        );

        const technique = printPosition?.printTechniques.find(
          (t) =>
            t.id === this.selectedPrintPosition.techniqueId ||
            t.printTechnique.id === this.selectedPrintPosition.techniqueId
        );

        const config = technique?.logoValidators.find(
          (v) => v.type.trim() === 'Resolution'
        )?.configuration;

        if (
          config &&
          affectedShape.kind === 'Logo' &&
          !affectedShape.isVectorFileTrueVectorFile
        ) {
          this.revalidateResolutionOnly(affectedShape, config);
        }
      }

      this.updateMirrorShape(affectedShape);

      const snapshot = createStateSnapshot();
      this.recordHistory(snapshot);

      const eventPayload = generateEventPayload();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });
    },

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    beforeUpdateShape(current: AnnotationShape) {
      // console.log('before update shape: ', shape);
    },

    beforeSelectShape(target: AnnotationShape) {
      this.selectShape(target.id);
    },

    beforeDeselectShape({
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      current,
      target,
    }: {
      current: AnnotationShape;
      target: AnnotationShape;
    }) {
      if (!target) {
        this.deselectShape();
      }
    },

    bringToFront(selectedShapeId: string) {
      const affectedShapeIndex = this.shapes.findIndex(
        (shape) => shape.id === selectedShapeId
      );
      const os = [...this.shapes];
      /**
       * Move the currently selected item to the end of list
       * thus making it appear on top of others (stack like)
       */
      os.push(os.splice(affectedShapeIndex, 1)[0]);
      /**
       * Update state, so that pintura gets newly oredered stack
       */
      this.shapeStore.$patch((state) => {
        state.shapes = os;
      });
    },

    setActiveShapeId(id: string) {
      if (id.length < 36) {
        return;
      }
      this.selectShape(id);
    },

    async removeShape(shape: AnnotationShape, createSnapshot: boolean) {
      const shapeStore = useShapeStore();
      const shapeToDelete = shapeStore.shapes.find((s) => s.id === shape.id);
      if (shapeToDelete && shapeToDelete.kind === 'Logo') {
        await this.removeArtworkPayload(shape.id, createSnapshot);
      } else {
        this.removeTextPayload(shape.id, createSnapshot);
      }

      this.revalidateArtworks(
        this.unavailablePrintPositions,
        this.selectedPrintPosition.id,
        this.selectedPrintPosition.id
      );

      const eventPayload = generateEventPayload();

      this.$emit('wizard:price-params-changed', {
        productPrintPositions: eventPayload,
      });
    },

    revalidateArtworks(
      event: ProductPrintPositionAddModel[],
      positionId: string,
      oldPositionId: string
    ) {
      const relevantEventData = event.find((e) => e.positionId === positionId);
      if (relevantEventData) {
        const activePrintPosition = this.data.productPrintPositions.find(
          (pp) => pp.id === relevantEventData.positionId
        );

        if (activePrintPosition) {
          const activePrintTechnique = activePrintPosition.printTechniques.find(
            (t) => t.id === relevantEventData.technique.id
          );

          if (activePrintTechnique) {
            const selectedPrintPosition = {
              ...activePrintPosition,
              techniqueId: activePrintTechnique.id,
              techniqueName: activePrintTechnique.printTechnique.name,
              maximumColours: activePrintTechnique.maximumColours,
            } as ProductPrintPositionBoxModel;

            const relevantShapeMap = this.positionShapeMap.filter(
              (p) => p.id === selectedPrintPosition.id
            );
            const relevantShapes = this.shapes.filter((a) => {
              return relevantShapeMap.find((s) => s.logoId === a.id);
            }) as AnnotationShape[];

            if (relevantShapes.length === 0) {
              this.resetAllValidatorPipelineResults(oldPositionId);
            }

            const allResults = [];

            for (const shape of relevantShapes) {
              if (shape && shape.kind === 'Logo') {
                // Constructing the validation pipeline
                const pipeline = constructArtworkValidationPipeline(
                  this.data,
                  [selectedPrintPosition],
                  relevantShapes,
                  shape,
                  false
                );

                // Invoking the validation pipeline
                const pipelineResult = pipeline({
                  payload: shape,
                  validationResult: [],
                });

                if (
                  DoSmallDetailsPart &&
                  shape.smallDetailsResult &&
                  shape.smallDetailsResult.status !== 'NoCheck'
                ) {
                  const smallDetailsValidator = {
                    printPositionId: positionId,
                    validatorName: 'smallDetails',
                    validatorColor: getSmallDetailsValidatorColor(
                      shape.smallDetailsResult.status
                    ),
                  };

                  allResults.push({
                    payload: shape,
                    validationResult: [smallDetailsValidator],
                  });
                }

                // Add the new validation results for the specific artwork to the array
                allResults.push(pipelineResult);
              }
            }

            if (relevantShapes.length > 0) {
              relevantShapes.forEach((element) => {
                this.resetNotificationsForOneShape(element.id);
              });
              this.revalidateColorsOnly();
            }

            this.setAllValidatorPipelineResults(allResults);
          }
        }
      }
    },

    async constructPersonalizationJSON() {
      const { sku, name } = this.data;
      const files = await getArtworkFiles(this.shapes);

      try {
        const { productVariants: pdpChoicesProductVariants } = this.$pdpChoices;

        const form: CreatePersonalizationForm = {
          externalCustomerId: this.$sapCustomerId,
          productSKU: sku,
          productName: name,
          isConcept: this.isConcept,
          isDirectApproval: this.$isDirectApproval,
          customerCompanyLogoUrl: this.$customerCompanyLogoUrl,
          contactLanguageCode: this.languageInUse,
          creator: '',
          proofType: 'AdvancedPersonalization',
          productVariants: pdpChoicesProductVariants,
          newProofForm: getProofFormFromState(false),
          remarkCodes: this.notificationsForBell
            .filter(
              (notification: PipelineNotificationType) =>
                notification.validatorName === 'commercialRemark'
            )
            .map((notification) => notification.message),
        } as unknown as CreatePersonalizationForm;

        return { form: form, files: files } as CreatePersonalizationFormWrapper;
      } catch (e: unknown) {
        console.log((e as Error).message);
      }
    },
  },
});
